import { colors } from '@alixpartners/ui-components';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { confirmExcelFile, uploadExcelFile, uploadFile } from 'utils/api';
import { IFileDataType } from 'utils/types';
import {
  AddFilePrimaryButton,
  AddFileSecondaryButton,
  UploadIcon,
  UploadTileDragText,
  UploadWrapper,
} from './UploadFileTile.styles';

export const UploadFileTile = ({
  fileType,
  fileUploadInProgress,
  isPrimaryButton,
  onFail,
  onSuccess,
  onStartUpload,
  onResetUpload,
}: {
  fileType: IFileDataType;
  fileUploadInProgress: boolean;
  isPrimaryButton: boolean;
  onFail: (param: {
    errorMessage: any;
    fileName: string;
    fileType: IFileDataType;
    id: number;
    statusCode: number;
  }) => void;
  onStartUpload: () => void;
  onSuccess: any;
  onResetUpload: () => void;
}) => {
  const onError = useCallback(
    (
      {
        fileName,
        id,
      }: {
        fileName: string;
        id: number;
      },
      err: any
    ) => {
      onFail({
        errorMessage: err.message,
        fileName,
        fileType,
        id,
        statusCode: err.statusCode,
      });
    },
    [fileType, onFail]
  );
  const onGetFilePath = useCallback(
    (
      {
        fileName,
        fileUrl,
        id,
      }: {
        fileName: string;
        fileUrl: string;
        id: number;
      },
      file: any
    ) => {
      uploadFile(fileUrl, file)
        .then(() => confirmExcelFile(String(id)))
        .then(
          () => onSuccess(id, fileType),
          (err) => onError({ fileName, id }, err)
        );
    },
    [fileType, onError, onSuccess]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      onStartUpload();
      acceptedFiles.forEach((file: any) => {
        uploadExcelFile(
          file,
          fileType,
          (props) => onGetFilePath(props, file),
          (err) =>
            onFail({
              statusCode: err.statusCode,
              errorMessage: err.message?.errors?.FileName[0],
              fileName: '',
              fileType,
              id: 0,
            })
        );
      });
    },
    [fileType, onFail, onGetFilePath, onStartUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  if (fileUploadInProgress) {
    return (
      <UploadWrapper isDragActive={false}>
        <LoadingIndicator loading label="File processing" />
        <AddFileSecondaryButton
          onClick={onResetUpload}
          iconName="baseline_cancel"
        >
          Cancel
        </AddFileSecondaryButton>
      </UploadWrapper>
    );
  }

  return (
    <UploadWrapper {...getRootProps()} isDragActive={isDragActive}>
      <input {...getInputProps()} multiple={false} />
      <UploadIcon
        iconName="cloud_upload"
        iconColor={colors.grey3}
        iconSize="90px"
      />
      <UploadTileDragText>
        Drag <strong>.csv</strong> file here
      </UploadTileDragText>
      <UploadTileDragText>or browse files to upload</UploadTileDragText>
      {isPrimaryButton ? (
        <AddFilePrimaryButton iconName="baseline_add">
          Add File
        </AddFilePrimaryButton>
      ) : (
        <AddFileSecondaryButton iconName="baseline_add">
          Add File
        </AddFileSecondaryButton>
      )}
    </UploadWrapper>
  );
};
